import { Flex, Spacer, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";

import { ProjectRead } from "@nens/rana/api-v1";
import { RanaHeading } from "@nens/ranalib";

import { getFolderTemplateQuery } from "@/queries/query-folder-template";
import { ProjectProvider } from "@/stores/project-provider";

import { ProjectFilesList } from "./project-files-list";

export function TenantProjectTemplate() {
  // The project template always exists, the backend will create one if it
  // didn't exist yet. So there is no UI needed for the case where it doesn't.
  const folderTemplateQuery = useQuery(getFolderTemplateQuery());
  const template = folderTemplateQuery.data;

  // There are quite a few places where ProjectFilesList assumes a ProjectContext to be
  // present, so we will provide one.
  // The template is missing some properties compared to ProjectRead, but they don't matter
  // in this case so we just add some empty data.
  // Also, for some reason the template id is nullable, but it cannot be null in practice.
  const project: ProjectRead | null =
    template && template.id
      ? {
          ...template,
          creator: {
            id: "",
            givenName: "",
            familyName: "",
            email: "",
          },
          lastActivity: new Date(),
          contributors: [],
        }
      : null;

  return (
    <Flex direction="column" gap={2}>
      <Flex flexWrap="wrap" alignItems="center">
        <RanaHeading type="h4">
          <FormattedMessage
            id="heading.project_template"
            defaultMessage="Set a folder template for all your projects"
          />
        </RanaHeading>
        <Spacer />
      </Flex>
      <Text maxW="prose">
        <FormattedMessage
          id="template.information"
          defaultMessage="All new projects will start with a copy of the folder structure you compose here."
        />
      </Text>
      <Text maxW="prose">
        <FormattedMessage
          id="template.largefileswarning"
          defaultMessage="Please pay attention when adding large files to the template. They may cause project
        creation to become slower and every new project will take up that much extra storage space."
        />
      </Text>
      {project ? (
        <ProjectProvider project={project}>
          <ProjectFilesList />
        </ProjectProvider>
      ) : null}
    </Flex>
  );
}
